// src/components/UserForm.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './style/UserForm.css';

const UserForm = ({ onUserCreated }) => {
  const initialFormState = {
      firstName: '', 
      lastName: '', 
      email: '', 
      jobTitle: '', 
      phone: '', 
      organisation: '', 
      role: '', 
      status: 'Active', 
      password: ''
    };

  const [formData, setFormData] = useState(initialFormState);
  const [orgOptions, setOrgOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [orgsData, rolesData] = await Promise.all([
          apiService.getOrganisations(),
          apiService.getGroups()
        ]);
        setOrgOptions(orgsData);
        setRoleOptions(rolesData);
        if (rolesData.length > 0) setFormData(prev => ({ ...prev, role: rolesData[0].roleKey }));
      } catch (err) {
        setError("Failed to load organisations or roles.");
      }
    };
    fetchDropdownData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    const submissionData = {
      username: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      jobTitle: formData.jobTitle,
      phone: formData.phone && formData.phone.trim() !== '' ? formData.phone : null,
      organisationId: parseInt(formData.organisation),
      role: formData.role,
      status: formData.status,
      password: formData.password
    };

    try {
      await apiService.createUser(submissionData);
      
      setSuccessMessage('User created successfully!');
      setFormData(initialFormState); 
      setShowPassword(false); // Reset password view
      
      if (roleOptions.length > 0) {
         setFormData(prev => ({ ...prev, role: roleOptions[0].roleKey }));
      }

      setTimeout(() => {
        if (onUserCreated) onUserCreated(); 
        setSuccessMessage('');
      }, 1500);

    } catch (err) {
      console.error("API Error:", err);
      if (err.status === 409) {
        setError('This email address is already in use.');
      } else {
        setError(err.message || 'Failed to create user.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // --- UPDATED LOGIC: Check Organisation Type ---
  const getFilteredRoles = () => {
    const selectedOrgId = parseInt(formData.organisation);
    const selectedOrg = orgOptions.find(org => org.id === selectedOrgId);
    
    if (!selectedOrg) return roleOptions;

    const orgType = selectedOrg.organisationType || selectedOrg.type || '';
    const isHeadOffice = orgType.trim().toLowerCase() === 'head office account';

    return roleOptions.filter(role => {
      const isAdminRole = role.roleKey === 'admin';
      if (isAdminRole) {
        return isHeadOffice;
      }
      return true;
    });
  };

  // --- NEW HELPER: Check if Contact Only ---
  const isContactOnly = formData.role === 'contact_only';

  return (
    <form className="uf-container" onSubmit={handleSubmit}>
      <h2>Add New User</h2>
      
      {error && <div className="uf-error-message">{error}</div>}
      {successMessage && <div className="uf-success-message">{successMessage}</div>}

      {/* Row 1: Names */}
      <div className="uf-group">
        <label className="uf-required">First Name</label>
        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="e.g. John" />
      </div>

      <div className="uf-group">
        <label className="uf-required">Last Name</label>
        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="e.g. Doe" />
      </div>

      {/* Row 2: Email (Full Width) */}
      <div className="uf-group uf-full-width">
        <label className="uf-required">User Email</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" />
      </div>

      {/* Row 3: Phone & Org */}
      <div className="uf-group">
        <label>Phone <span className="uf-optional">(Optional)</span></label>
        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
      </div>

      <div className="uf-group">
        <label className="uf-required">Organisation</label>
        <select name="organisation" value={formData.organisation} onChange={handleChange} required>
          <option value="">Select Organisation</option>
          {orgOptions.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
        </select>
      </div>

      {/* Row 4: Role & Status */}
      <div className="uf-group">
        <label className="uf-required">Role</label>
        <select name="role" value={formData.role} onChange={handleChange} required>
          <option value="">Select Role</option>
          {getFilteredRoles().map(role => (
            <option key={role.id} value={role.roleKey}>{role.name}</option>
          ))}
        </select>
      </div>

      <div className="uf-group">
        <label>Status</label>
        <select name="status" value={formData.status} onChange={handleChange}>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* Row 5: Password (Full Width) with Toggle */}
      {/* --- CHANGE: Hide if Contact Only --- */}
      {!isContactOnly && (
        <div className="uf-group uf-full-width">
          <label className="uf-required">Password</label>
          <div className="uf-password-wrapper">
            <input 
              type={showPassword ? "text" : "password"} 
              name="password" 
              value={formData.password} 
              onChange={handleChange} 
              required={!isContactOnly} 
            />
            <button 
              type="button" 
              className="uf-password-toggle" 
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="uf-actions">
        <button type="submit" disabled={isSubmitting || successMessage}>
          {isSubmitting ? 'Creating...' : successMessage ? 'Saved!' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
